import { useState } from "react";
import { Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { FileUploadDragAndDrop } from "@components/FormsV2/FileUploadDragAndDrop";
import { isAllowedFile, type ProjectAttachmentFormValues } from "../types";

// ~5GB direct upload ceiling (matches DocumentTemplates).
const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

export interface ProjectAttachmentFormProps {
  id: string;
  mode: "new" | "edit";
  initialValues: ProjectAttachmentFormValues;
  typeOptions: { value: string; label: string }[];
  currentFilename: string | null;
  onDownload?: () => void;
  onSubmit: (values: ProjectAttachmentFormValues) => void;
}

export const ProjectAttachmentForm = ({
  id,
  mode,
  initialValues,
  typeOptions,
  currentFilename,
  onDownload,
  onSubmit,
}: ProjectAttachmentFormProps): JSX.Element => {
  const [attachmentTypeId, setAttachmentTypeId] = useState(
    initialValues.attachmentTypeId
  );
  const [file, setFile] = useState<File | null>(initialValues.file);
  const [error, setError] = useState<string | null>(null);

  const noneOption = { value: "", label: "None" };
  const selectOptions = [noneOption, ...typeOptions];
  const selected =
    selectOptions.find((o) => o.value === attachmentTypeId) ?? noneOption;

  const handleFileSelect = (f: File) => {
    if (!isAllowedFile(f)) {
      setError("That file type is not supported.");
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mode === "new" && !file) {
      setError("You must upload a file before you can save the attachment.");
      return;
    }
    onSubmit({ attachmentTypeId, file });
  };

  return (
    <Form
      id={id}
      data-testid={id}
      onSubmit={handleSubmit}
      style={{ marginLeft: 16 }}
    >
      <div className="row">
        <div className="col-sm-4">
          <RequiredReactSelect
            label="Attachment Type"
            value={selected}
            onChange={(opt: any) => setAttachmentTypeId(opt?.value ?? "")}
            options={selectOptions}
            placeholder="None"
          />
        </div>
      </div>

      <div className="row" style={{ marginTop: 20 }}>
        <div className="col">
          <Form.Label>
            {mode === "edit" ? "Replace File" : "Upload File *"}
          </Form.Label>
          {mode === "edit" && currentFilename && (
            <div style={{ marginBottom: 8 }}>
              Current: <span>{currentFilename}</span>
              {onDownload && (
                <Button
                  variant="link"
                  className="p-0 ms-2"
                  onClick={onDownload}
                >
                  Download Current
                </Button>
              )}
            </div>
          )}
          {file ? (
            <div>
              <span>
                {file.name}{" "}
                <FontAwesomeIcon
                  style={{ color: "red", cursor: "pointer", marginLeft: 5 }}
                  icon={faTrashCan}
                  onClick={() => setFile(null)}
                />
              </span>
            </div>
          ) : (
            <FileUploadDragAndDrop
              handleFileSelect={handleFileSelect}
              maxFileSize={MAX_FILE_SIZE}
              text={
                mode === "edit"
                  ? "Click or drag and drop a file to replace the current attachment"
                  : undefined
              }
            />
          )}
          {error && (
            <div style={{ color: "#b00020", marginTop: 6 }} role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </Form>
  );
};

export default ProjectAttachmentForm;
